import { DateTime } from 'luxon';
import { TFilters, TFiltersQ4Us } from '../../redux/types';
import { DATES_ARRAY, DATES_ID } from '../../services/constants/dates-constants';
import { ETableStructureType, TTableStructure } from '../../ui/EasTable/types';

/**
 * Value a `separateFilter` column is applied with. The url wins; the column default fills in while
 * the url carries no `f-<selector>` param at all, and an empty param means the filter was cleared on
 * purpose — that is what keeps a cleared default from coming back.
 */
export const getSeparateFilterValue = (
	column: TTableStructure<any> | undefined,
	navigateParams: any,
): string => {
	if (!column) return '';
	const param = navigateParams[`f-${String(column.selector)}`];
	if (param === undefined || param === null) return column.defaultSeparateFilterValue ?? '';
	return String(param);
};

/**
 * Value a column is filtered by, whichever filter style it uses: a `separateFilter` column keeps it
 * under the `f-` prefix, a plain `filterable` one under the selector itself. Reading through this
 * lets a column switch between the two without touching the page that builds the request.
 */
export const getColumnFilterValue = (
	column: TTableStructure<any> | undefined,
	navigateParams: any,
): string => {
	if (!column) return '';
	if (column.separateFilter) return getSeparateFilterValue(column, navigateParams);
	return String(navigateParams[String(column.selector)] ?? '');
};

export const getFiltersFromNavigateParams = (
	tableStructure: TTableStructure<any>[],
	navigateParams: any,
) => {
	const navigateParamsWithoutPage = tableStructure.reduce(
		(acc, item) => {
			if (item.separateFilter) {
				const separateValue = getSeparateFilterValue(item, navigateParams);
				if (separateValue) {
					acc.push({
						fieldName: item.selector,
						name: item.selector as string,
						value: separateValue as string,
						operator: '=',
						comparator: '=',
					});
				}
				return acc;
			}
			if (!!navigateParams[item.selector] && item.type === ETableStructureType.DATE_RANGE) {
				if (navigateParams[item.selector] === DATES_ID.CUSTOM_RANGE) {
					const from = navigateParams[`${item.selector}StartDate`] as string | undefined;
					const to = navigateParams[`${item.selector}EndDate`] as string | undefined;
					if (from && to) {
						acc.push({
							fieldName: item.selector,
							name: item.selector,
							value: `[${from},${to}]`,
							operator: '=',
							comparator: '=',
						});
					}
					return acc;
				}
				const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector]);
				if (dates?.from && dates?.to) {
					acc.push({
						fieldName: item.selector,
						name: item.selector,
						value: `[${dates.from},${dates.to}]`,
						operator: '=',
						comparator: '=',
					});
				}
				return acc;
			}
			if (
				!!navigateParams[item.selector] &&
				(item.type === ETableStructureType.DATE || item.type === ETableStructureType.DATE_TIME)
			) {
				if (navigateParams[item.selector] === DATES_ID.CUSTOM_RANGE) {
					if (navigateParams[`${item.selector}StartDate`]) {
						acc.push({
							fieldName: item.selector,
							name: item.selector,
							value: navigateParams[`${item.selector}StartDate`] as string,
							operator: '>=',
							comparator: '>=',
						});
					}
					if (navigateParams[`${item.selector}EndDate`]) {
						acc.push({
							fieldName: item.selector,
							name: item.selector,
							value: DateTime.fromISO(navigateParams[`${item.selector}EndDate`] as string)
								.plus({ days: 1 })
								.toISODate() as string,
							operator: '<',
							comparator: '<',
						});
					}
					return acc;
				}
				const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector]);
				// console.log('dates', dates);
				if (dates?.from) {
					acc.push({
						fieldName: item.selector,
						name: item.selector,
						value: dates.from,
						operator: dates.comparatorFrom,
						comparator: dates.comparatorFrom,
					});
				}
				if (dates?.to) {
					acc.push({
						fieldName: item.selector,
						name: item.selector,
						value: dates.to,
						operator: dates.comparatorTo,
						comparator: dates.comparatorTo,
					});
				}
				return acc;
			}
			if (item.selector === 'schema_list' && navigateParams[item.selector]) {
				acc.push({
					name: item.selector as string,
					fieldName: item.selector,
					value: navigateParams[item.selector] as string,
					operator: '@>',
					comparator: '@>',
				});
				return acc;
			}
			if (navigateParams[item.selector])
				acc.push({
					fieldName: item.selector,
					name: item.selector,
					value: navigateParams[item.selector] as string,
					operator: '=',
					comparator: '=',
				});
			return acc;
		},
		[] as TFiltersQ4Us | TFilters,
	);
	if (navigateParams.searchEmId) {
		const emIdSelector =
			tableStructure.findIndex((item) => item.selector === 'em_id') === -1 ? 'emId' : 'em_id';
		navigateParamsWithoutPage.push({
			name: emIdSelector,
			fieldName: emIdSelector,
			value: navigateParams.searchEmId as string,
			comparator: '=',
			operator: '=',
		});
	}
	// if (navigateParams.search) {
	// 	navigateParamsWithoutPage.push({
	// 		name: 'external_order_id',
	// 		fieldName: 'external_order_id',
	// 		value: navigateParams.search as string,
	// 		operator: '=',
	// 		comparator: '=',
	// 	});
	// }

	return navigateParamsWithoutPage;
};
